import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useSignedInUsersWeekStartsOn } from './useSignedInUsersWeekStartsOn'
import { useWeekDates } from './useWeekDates'
import { WeekScope, WeekStartsOn } from 'providers/WeekScopeProvider'

interface WeekSelection {
  start: DateTime
  weekStartsOn: WeekStartsOn
}

interface Options {
  /**
   * Ran after the selected week or start day changes, for state belonging to
   * the week being navigated away from — an open sidebar, say.
   */
  onWeekChange?: () => void
}

/**
 * Establishes a week scope for the multi-worker grids, where the week is
 * seeded from the signed-in user and then re-selectable through the week
 * picker. Pass the returned `scope` to a WeekScopeProvider so the cells
 * inside the grid resolve to the same week the columns are drawn from.
 */
export const useSignedInUsersWeekScope = ({ onWeekChange }: Options = {}) => {
  const signedInUsersWeekStartsOn = useSignedInUsersWeekStartsOn()
  const [weekStartsOn, setWeekStartsOn] = useState(signedInUsersWeekStartsOn)
  const { end, range, set, start } = useWeekDates({ weekStartsOn })

  const scope: WeekScope = useMemo(
    () => ({ end, range, start, weekStartsOn }),
    [end, range, start, weekStartsOn],
  )

  const onWeekSelect = (selection: WeekSelection) => {
    setWeekStartsOn(selection.weekStartsOn)
    set(selection)
    onWeekChange?.()
  }

  return { ...scope, onWeekSelect, scope }
}
